import React from 'react';
import ReactECharts from 'echarts-for-react';
import { FindLoad } from 'utils';
import moment from 'moment';
import {
    buildBaselinePhaseSeries,
    buildPhaseChangeMarkLine,
    getBaselinePhaseLayout,
} from '../../../../fragments/client/clientSingle/clientClinical/goals/clinicalInsights/components/baselinePhase.helpers';
import {
    formatChartValueByDataType,
    isPercentageDataType,
} from '../../../../fragments/client/clientSingle/clientClinical/goals/clinicalInsights/components/dataTypeChart.helpers';
import {
    buildAverageLine,
    buildLinearTrendLine,
} from '../../../../fragments/client/clientSingle/clientClinical/goals/clinicalInsights/components/trendLine.helpers';

const COLORS = ['#0C66E4', '#36B37E', '#FF5630', '#FFAB00', '#6554C0', '#00B8D9', '#FF8B00', '#8777D9'];
const GOAL_EVENT_COLOR = '#FF5630';
const CLIENT_EVENT_COLOR = '#6554C0';
const TARGET_EVENT_COLOR = '#FF8B00';
const MASTERY_COLOR = '#36B37E';
const TREND_COLOR = '#FF6B6B';
const AVERAGE_COLOR = '#7A5AF8';
const TREND_SERIES_SUFFIX = ' Trend';

export const FormLineChart = ({
    categories = [],
    series = [],
    actionType,
    item,
    timeGroupBy = 'month',
    dataType,
    isPercent = true,
    clientEvents = [],
    goalEvents = [],
    targetEvents = [],
    goalMastery = [],
    masteryLabel = 'Goal Mastery',
    seriesConfig = [],
    showTrendLine = false,
    showAverageLine = false,
    // Count scale (Cumulative in Count mode): y-axis runs 0..countMax in whole steps.
    countMax,
    // TARGET "Show Target Baseline": the ACTIVE baseline, [{ date, value }] one per session, drawn as its own
    // phase left of the range (see baselinePhase.helpers).
    baselinePhase,
}) => {
    const loadData = FindLoad(actionType);
    const shouldUsePercentScale = dataType ? isPercentageDataType(dataType) : isPercent;
    const phaseLayout = getBaselinePhaseLayout(baselinePhase, categories.length);
    const axisCategories = [...phaseLayout.leading, ...categories];
    const formatCategoryLabel = (value, long = false) => {
        if (!value) return value;
        const phaseLabel = phaseLayout.formatKey(value, long);
        if (phaseLabel !== null) return phaseLabel;

        if (timeGroupBy === 'week') {
            const weekDate = moment(value, 'GGGG-[W]WW', true);
            return weekDate.isValid() ? weekDate.startOf('isoWeek').format(long ? 'DD MMM YYYY' : 'DD MMM') : value;
        }

        if (timeGroupBy === 'day') {
            const dayDate = moment(value, 'YYYY-MM-DD', true);
            return dayDate.isValid() ? dayDate.format(long ? 'DD MMM YYYY' : 'DD MMM') : value;
        }

        const monthDate = moment(value, 'YYYY-MM', true);
        return monthDate.isValid() ? monthDate.format(long ? 'MMM YYYY' : 'MMM') : value;
    };

    const STYLE_MAP = { Solid: 'solid', Dashed: 'dashed', Dotted: 'dotted' };
    const POINT_MAP = { Circle: 'circle', Square: 'rect', Diamond: 'diamond', None: 'none' };
    const parseThickness = (t) => parseInt(t) || 2;

    // Build markLine data for vertical event lines and horizontal mastery lines
    const markLineData = [];
    const stripHtml = (html) => html?.replace?.(/<[^>]*>/g, '')?.trim() || '';

    // Helper: match event date to a category (exact match or prefix match for monthly categories)
    const matchDateToCategory = (eventDate) => {
        const dateStr = eventDate?.split?.('T')?.[0] || eventDate || '';
        // Exact match
        if (categories.includes(dateStr)) return dateStr;
        // Prefix match: e.g. "2026-03-15" starts with category "2026-03"
        const matched = categories.find((cat) => dateStr.startsWith(cat));
        if (matched) return matched;
        return dateStr;
    };

    // ... markLineData building (unchanged) ...
    // Goal Events — vertical lines (red)
    goalEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        const reason = stripHtml(event?.reason);
        const staff = event?.staff?.fullName || '';
        const date = event?.eventDate || '';
        markLineData.push({
            xAxis: dateLabel,
            label: {
                formatter: event?.name || event?.title || 'Goal Event',
                position: 'end',
                fontSize: 10,
                color: GOAL_EVENT_COLOR,
            },
            lineStyle: {
                color: GOAL_EVENT_COLOR,
                type: 'dashed',
                width: 2,
            },
            emphasis: {
                lineStyle: { width: 3 },
            },
            tooltip: {
                show: true,
                formatter: () => {
                    let html = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${GOAL_EVENT_COLOR};margin-right:5px;"></span><b>Goal Event</b>`;
                    if (date) html += ` <span style="color:#888;">(${moment(date).format('MM/DD/YY')})</span>`;
                    if (reason) html += `<br/><span style="padding-left:15px;color:#555;">${reason}</span>`;
                    if (staff) html += `<br/><span style="padding-left:15px;color:#888;font-size:11px;">— ${staff}</span>`;
                    return html;
                },
            },
        });
    });

    // Client Events — vertical lines (purple)
    clientEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        const reason = stripHtml(event?.reason);
        const staff = event?.staff?.fullName || '';
        const date = event?.eventDate || '';
        markLineData.push({
            xAxis: dateLabel,
            label: {
                formatter: event?.name || event?.title || 'Client Event',
                position: 'end',
                fontSize: 10,
                color: CLIENT_EVENT_COLOR,
            },
            lineStyle: {
                color: CLIENT_EVENT_COLOR,
                type: 'dotted',
                width: 2,
            },
            emphasis: {
                lineStyle: { width: 3 },
            },
            tooltip: {
                show: true,
                formatter: () => {
                    let html = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${CLIENT_EVENT_COLOR};margin-right:5px;"></span><b>Client Event</b>`;
                    if (date) html += ` <span style="color:#888;">(${moment(date).format('MM/DD/YY')})</span>`;
                    if (reason) html += `<br/><span style="padding-left:15px;color:#555;">${reason}</span>`;
                    if (staff) html += `<br/><span style="padding-left:15px;color:#888;font-size:11px;">— ${staff}</span>`;
                    return html;
                },
            },
        });
    });

    // Target Events — vertical lines (orange)
    targetEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        const reason = stripHtml(event?.reason);
        const staff = event?.staff?.fullName || '';
        const date = event?.eventDate || '';
        markLineData.push({
            xAxis: dateLabel,
            label: {
                formatter: event?.name || event?.title || 'Target Event',
                position: 'end',
                fontSize: 10,
                color: TARGET_EVENT_COLOR,
            },
            lineStyle: {
                color: TARGET_EVENT_COLOR,
                type: [5, 3, 2, 3],
                width: 2,
            },
            emphasis: {
                lineStyle: { width: 3 },
            },
            tooltip: {
                show: true,
                formatter: () => {
                    let html = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${TARGET_EVENT_COLOR};margin-right:5px;"></span><b>Target Event</b>`;
                    if (date) html += ` <span style="color:#888;">(${moment(date).format('MM/DD/YY')})</span>`;
                    if (reason) html += `<br/><span style="padding-left:15px;color:#555;">${reason}</span>`;
                    if (staff) html += `<br/><span style="padding-left:15px;color:#888;font-size:11px;">— ${staff}</span>`;
                    return html;
                },
            },
        });
    });

    // Goal Mastery — horizontal lines (green)
    goalMastery.forEach((mastery) => {
        const value = mastery?.count ?? mastery?.value ?? mastery;
        if (value !== undefined && value !== null) {
            markLineData.push({
                yAxis: value,
                label: {
                    formatter: mastery?.name || mastery?.title || masteryLabel,
                    position: 'insideEndTop',
                    fontSize: 10,
                    color: MASTERY_COLOR,
                },
                lineStyle: {
                    color: MASTERY_COLOR,
                    type: 'dashed',
                    width: 2,
                },
            });
        }
    });

    // Build a map of category date -> event reasons for tooltip
    const eventReasonMap = {};

    goalEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        if (!eventReasonMap[dateLabel]) eventReasonMap[dateLabel] = [];
        eventReasonMap[dateLabel].push({
            type: 'Goal Event',
            color: GOAL_EVENT_COLOR,
            reason: stripHtml(event?.reason),
            staff: event?.staff?.fullName || '',
            date: event?.eventDate || '',
        });
    });

    clientEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        if (!eventReasonMap[dateLabel]) eventReasonMap[dateLabel] = [];
        eventReasonMap[dateLabel].push({
            type: 'Client Event',
            color: CLIENT_EVENT_COLOR,
            reason: stripHtml(event?.reason),
            staff: event?.staff?.fullName || '',
            date: event?.eventDate || '',
        });
    });

    targetEvents.forEach((event) => {
        const dateLabel = matchDateToCategory(event?.eventDate);
        if (!eventReasonMap[dateLabel]) eventReasonMap[dateLabel] = [];
        eventReasonMap[dateLabel].push({
            type: 'Target Event',
            color: TARGET_EVENT_COLOR,
            reason: stripHtml(event?.reason),
            staff: event?.staff?.fullName || '',
            date: event?.eventDate || '',
        });
    });

    const chartSettings = item?.properties?.chartSettings || {};
    const customizeSettings = chartSettings?.customize || {};
    const bgColor = customizeSettings?.background || '#ffffff';
    const borderColor = customizeSettings?.border || '#E4E7EC';
    const isSmooth = customizeSettings?.smooth !== false;
    const connectNulls = !!customizeSettings?.plotNull;

    const chartSeries = series
        .map((s, i) => {
            // Get per-series config only for this exact index
            const cfg = seriesConfig[i] || {};

            // Skip hidden series
            if (cfg.visible === false) return null;

            const color = cfg.color || COLORS[i % COLORS.length];
            const lineWidth = parseThickness(cfg.thickness);
            const lineType = STYLE_MAP[cfg.style] || 'solid';
            const symbol = POINT_MAP[cfg.points] || 'circle';

            return {
                name: s.name || `Series ${i + 1}`,
                type: 'line',
                smooth: isSmooth,
                connectNulls: connectNulls,
                data: phaseLayout.pad(s.data || []),
                symbol: symbol,
                symbolSize: symbol === 'none' ? 0 : 6,
                // Draw every point: ECharts' default ('auto') hides symbols on a dense category axis, and a session
                // with empty days around it has no line either — it would not show at all.
                showAllSymbol: true,
                lineStyle: { color, width: lineWidth, type: lineType },
                itemStyle: { color },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: color + '26' },
                            { offset: 1, color: color + '03' },
                        ],
                    },
                },
            };
        })
        .filter(Boolean);

    // Trend (linear regression) and Average (horizontal mean) overlays per visible series.
    // Single-series charts use the shared insights colors (red trend / purple average);
    // multi-series (Target Overview) charts reuse each series' own color so overlays stay attributable.
    const visibleSeriesCount = series.filter((s, i) => (seriesConfig[i] || {}).visible !== false).length;
    if (showTrendLine || showAverageLine) {
        const overlayDecimals = shouldUsePercentScale ? 1 : 2;
        series.forEach((s, i) => {
            const cfg = seriesConfig[i] || {};
            if (cfg.visible === false) return;
            const seriesColor = cfg.color || COLORS[i % COLORS.length];
            const values = (s.data || []).map((value) => {
                if (value === null || value === undefined) return null;
                const numeric = Number(value);
                return Number.isNaN(numeric) ? null : numeric;
            });

            if (showAverageLine) {
                const averageLine = buildAverageLine({ values, decimals: overlayDecimals });
                if (averageLine.value !== null) {
                    const avgColor = visibleSeriesCount > 1 ? seriesColor : AVERAGE_COLOR;
                    const formattedAvg = dataType
                        ? formatChartValueByDataType(averageLine.value, dataType)
                        : `${averageLine.value}${isPercent ? '%' : ''}`;
                    markLineData.push({
                        yAxis: averageLine.value,
                        label: {
                            formatter: `Avg (${formattedAvg})`,
                            position: 'insideEndBottom',
                            fontSize: 10,
                            color: avgColor,
                        },
                        lineStyle: { color: avgColor, type: 'dashed', width: 1.5 },
                    });
                }
            }

            if (showTrendLine) {
                const numericValues = values.filter((value) => value !== null);
                const trendMax = shouldUsePercentScale ? 100 : countMax || Math.max(Math.max(...numericValues, 0) * 1.2, 1);
                const trendLine = buildLinearTrendLine({ values, maxValue: trendMax, decimals: overlayDecimals });
                if (trendLine.seriesData.length > 0) {
                    chartSeries.push({
                        name: `${s.name || `Series ${i + 1}`}${TREND_SERIES_SUFFIX}`,
                        type: 'line',
                        data: phaseLayout.pad(trendLine.seriesData),
                        symbol: 'none',
                        silent: true,
                        lineStyle: {
                            color: visibleSeriesCount > 1 ? seriesColor : TREND_COLOR,
                            width: 2,
                            type: visibleSeriesCount > 1 ? 'dashed' : 'solid',
                            opacity: 0.95,
                        },
                        z: 3,
                    });
                }
            }
        });
    }

    chartSeries.push(...buildBaselinePhaseSeries(phaseLayout, categories.length));
    if (phaseLayout.leading.length) markLineData.push(buildPhaseChangeMarkLine());

    // Attach markLine data to the first visible series
    if (chartSeries.length > 0 && markLineData.length > 0) {
        chartSeries[0].markLine = {
            symbol: 'none',
            triggerEvent: true,
            data: markLineData,
        };
    }

    // If no series but we have markLine data, create a dummy series to show annotations
    if (chartSeries.length === 0 && markLineData.length > 0) {
        chartSeries.push({
            name: '',
            type: 'line',
            data: [],
            markLine: {
                symbol: 'none',
                data: markLineData,
            },
        });
    }

    const bottomText = chartSettings?.bottomText;
    const leftText = chartSettings?.leftSideText;
    const titleText = chartSettings?.chartTitle;
    const subtitleText = chartSettings?.chartSubtitle;

    const option = {
        animation: false,
        backgroundColor: bgColor,
        title: {
            text: titleText,
            subtext: subtitleText,
            left: 'center',
        },
        graphic: [
            {
                id: 'leftText',
                type: 'text',
                left: 0,
                top: 'middle',
                rotation: -Math.PI / 2,
                style: {
                    text: leftText || '',
                    fill: '#333',
                    fontSize: 14,
                },
            },
            {
                id: 'bottomText',
                type: 'text',
                left: 'center',
                bottom: 5,
                style: {
                    text: bottomText || '',
                    fill: '#333',
                    fontSize: 14,
                },
            },
        ],

        tooltip: {
            trigger: 'axis',
            formatter: (params) => {
                if (!params || !params.length) return '';
                const categoryDate = params[0]?.axisValue || '';
                if (phaseLayout.formatKey(categoryDate) === '') return '';
                const displayDate = formatCategoryLabel(categoryDate, true);
                let html = `<b>${displayDate}</b>`;

                // Standard series data (trend overlays are excluded from the tooltip)
                params.forEach((p) => {
                    if (p.seriesName?.endsWith(TREND_SERIES_SUFFIX)) return;
                    if (p.value !== undefined && p.value !== null && p.seriesName) {
                        html += `<br/><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:5px;"></span>${p.seriesName}: <b>${dataType ? formatChartValueByDataType(p.value, dataType) : `${p.value}${isPercent ? '%' : ''}`}</b>`;
                    }
                });

                // Event reasons
                const events = eventReasonMap[categoryDate];
                if (events?.length) {
                    html += '<br/><hr style="margin:4px 0;border:none;border-top:1px solid #e5e7eb;"/>';
                    events.forEach((ev) => {
                        html += `<br/><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${ev.color};margin-right:5px;"></span><b>${ev.type}</b>`;
                        if (ev.date) html += ` <span style="color:#888;">(${moment(ev.date).format('MM/DD/YY')})</span>`;
                        if (ev.reason) html += `<br/><span style="padding-left:15px;color:#555;">${ev.reason}</span>`;
                        if (ev.staff) html += `<br/><span style="padding-left:15px;color:#888;font-size:11px;">— ${ev.staff}</span>`;
                    });
                }

                return html;
            },
        },
        legend: {
            data: [...series.map((s, i) => s.name || `Series ${i + 1}`), ...(phaseLayout.phase.length ? ['Baseline'] : [])],
            bottom: bottomText ? 25 : 0,
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: bottomText ? '18%' : '12%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: axisCategories,
            axisLine: { lineStyle: { color: '#E5E7EB' } },
            axisLabel: {
                color: '#626F86',
                fontSize: 12,
                formatter: (value) => formatCategoryLabel(value, false),
            },
        },
        yAxis: {
            type: 'value',
            axisLine: { show: false },
            axisLabel: {
                color: '#626F86',
                fontSize: 12,
                formatter: (value) =>
                    dataType ? formatChartValueByDataType(value, dataType) : shouldUsePercentScale ? `${value}%` : value,
            },
            splitLine: { lineStyle: { color: '#F3F4F6' } },
            ...(shouldUsePercentScale ? { min: 0, max: 100 } : countMax ? { min: 0, max: countMax, minInterval: 1 } : {}),
        },
        series: chartSeries,
    };

    return (
        <ReactECharts
            showLoading={loadData?.length}
            option={option}
            notMerge={true}
            style={{ height: 300, width: '100%', border: `1px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden' }}
            opts={{ renderer: 'svg' }}
        />
    );
};
